import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { InputProp } from '../../utils/tsConfig';

interface SearchBoxProps extends InputProp {
  data?: any[]; // Must be an array!
  searchKey?: string | string[] | null;
  debounceTime?: number;
  placeholder?: string;
  className?: string;
  onFilter?: (filtered: any[]) => void;
  autoFocus?: boolean;
}

const Searchbox: React.FC<SearchBoxProps> = ({
  onChange,
  state,
  setState,
  fieldName,
  keyId,
  returnKey,
  data = [], // default to empty array
  searchKey = null,
  debounceTime = 300,
  placeholder = 'Search...',
  className = '',
  onFilter,
  autoFocus = false,
}) => {
  const [searchTerm, setSearchTerm] = useState<string>((state as string) || '');

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    setState?.(value);
    onChange?.({ name: fieldName, value });
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    setState?.('');
    onChange?.({ name: fieldName, value: '' });
  };

  const filteredData = useMemo(() => {
    if (!Array.isArray(data) || !searchTerm) return [];

    const term = searchTerm.toLowerCase();
    const keys = Array.isArray(searchKey) ? searchKey : searchKey ? [searchKey] : [];

    return data.filter(item => {
      if (keys.length > 0) {
        return keys.some(key => {
          const value = key.split('.').reduce((obj, k) => obj?.[k], item);
          return value?.toString().toLowerCase().includes(term);
        });
      } else {
        // If no searchKey provided, search all string/number fields
        return Object.values(item).some(val =>
          typeof val === 'string' || typeof val === 'number'
            ? val.toString().toLowerCase().includes(term)
            : false
        );
      }
    });
  }, [searchTerm, data, searchKey]);

  useEffect(() => {
    if (onFilter) {
      const timeout = setTimeout(() => {
        onFilter(filteredData);
      }, debounceTime);

      return () => clearTimeout(timeout);
    }
  }, [filteredData, onFilter, debounceTime]);

  return (
    <div className={className || 'flex justify-center items-center w-full'}>
      <div className="relative w-full max-w-md">
        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg className="w-4 h-4 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
            <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
          </svg>
        </div>
        <input
          id={keyId}
          name={fieldName}
          type="text"
          autoFocus={autoFocus}
          value={searchTerm}
          onChange={handleInputChange}
          placeholder={placeholder}
          data-return-key={returnKey}
          className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClearSearch}
            className="absolute inset-y-0 right-0 flex items-center pr-3"
            aria-label="Clear search"
          >
            <svg className="w-4 h-4 text-gray-500 hover:text-gray-700" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
              <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 12 12M1 13 13 1" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default Searchbox;

// {/* <Input2
//         type="searchbar"
//         fieldName="search"
//         placeholder="Search by name, email, or ID"
//         setState={setSearch}
//         state={search}
//         data={students}
//         searchKey={['name', 'email', 'studentId']} // ✅ multiple keys
//         onFilter={setFilteredData}
//       /> */}